/**
 * `/settings/audit-export` — the managing-partner-only firm-wide audit surface
 * (routing-and-state.md §1, under `_partner-only`; T-6.14 fills the Sprint-1
 * placeholder). Shows the last daily integrity-check verdict (T-6.15 —
 * `GET /audit/integrity`) and the firm-wide NDJSON export (audit.export;
 * audited server-side as `audit_export_requested`). The two-guard wall stands:
 * `firm_admin` reaches `_settings` but NOT this route (§2.1 case-data wall —
 * the firm-wide log carries case-linked content).
 *
 * Built on the design-system primitives (UI/UX Flow 25).
 */
import { Download } from '../../components/ui/icons.ts';
import type { JSX } from 'react';

import { IntegrityStatus } from '../../components/audit/integrity-verdict.tsx';
import {
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  buttonClassName,
} from '../../components/ui/index.ts';
import { api } from '../../lib/api.ts';

import '../../styles/audit.css';

export function AuditExportScreen(): JSX.Element {
  return (
    <div className="argus-page">
      <PageHeader
        title="Audit export"
        description="Export the firm's complete tamper-evident audit log. Every export is itself recorded on the chain."
      />

      <Card>
        <CardHeader
          title="Daily integrity check"
          description="The whole chain is re-hashed every day at 03:00 UTC; this is the last verdict."
        />
        <CardBody>
          <IntegrityStatus />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Firm-wide export (NDJSON)"
          description="One JSON object per audit event, carrying every hash-chain field. A third party can re-compute the whole chain from this file with the published verifier. Managing partner only."
        />
        <CardBody>
          <div className="stack">
            <div className="row">
              <a
                href={api.audit.firmExportUrl()}
                className={buttonClassName({ variant: 'primary' })}
              >
                <Download size={16} aria-hidden="true" />
                Download audit log (.ndjson)
              </a>
            </div>
            <p className="text-muted text-xs">
              Verification bundles per case (CSV, certificate and the embedded Python verifier) are
              exported from each case's Audit trail page. On-chain anchoring is pending V1 GA; the
              in-database chain is fully verifiable today.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
